import type { PatchRequestType } from '@onhandtech/onhand-fe-shared/lib/api/v4/requests/post-patch-request-by-id/post-patch-request-by-id.types'
import type {
  NormalisedRequest,
  RequestStatus,
} from '@onhandtech/onhand-fe-shared/lib/api/v4/requests/requests.types'
import type { OrderBy } from '@onhandtech/onhand-fe-shared/lib/contracts/api/v4/requests/requests'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import type { MissionFilterType } from '../../../pages/admin/content/sub-sections/missions/constants'

import type { MissionsState } from './missions.types'
import { moduleName } from './missions.types'

const initialState: MissionsState = {
  autoEnrollUser: true,
  draftRequest: null,
  activeMissionType: 'all',
  numberOfMissionsConfig: {
    APPROVED: null,
    PENDING: null,
    REJECTED: null,
  },
  orderBy: 'created_on',
  patchRequestType: null,
  searchQuery: '',
  selectedRequest: null,
  selectedStatusOption: 'PENDING',
  shouldShowBeneficiaryIndicator: false,
  showRejectionScreen: false,
}

const slice = createSlice({
  name: moduleName,
  initialState,
  reducers: {
    setSelectedRequest: (state: MissionsState, action: PayloadAction<NormalisedRequest | null>) => {
      state.selectedRequest = action.payload
      state.draftRequest = action.payload
      state.shouldShowBeneficiaryIndicator = !!action.payload?.manualBeneficiary
    },
    clearSelectedRequest: (state: MissionsState) => {
      state.selectedRequest = null
      state.draftRequest = null
      state.autoEnrollUser = true
      state.showRejectionScreen = false
      state.patchRequestType = null
      state.shouldShowBeneficiaryIndicator = false
    },
    setPatchRequestType: (state: MissionsState, action: PayloadAction<PatchRequestType | null>) => {
      state.patchRequestType = action.payload
    },
    setAutoEnroll: (state: MissionsState, action: PayloadAction<boolean>) => {
      state.autoEnrollUser = action.payload
    },
    setShowRejectionScreen: (state: MissionsState, action: PayloadAction<boolean>) => {
      state.showRejectionScreen = action.payload
    },
    setMissionType: (state: MissionsState, action: PayloadAction<MissionFilterType>) => {
      state.activeMissionType = action.payload
    },
    updateDraftRequest: (
      state: MissionsState,
      action: PayloadAction<Partial<NormalisedRequest>>,
    ) => {
      if (state.draftRequest && state.selectedRequest) {
        state.draftRequest = {
          ...state.draftRequest,
          ...action.payload,
        }

        return
      }

      state.draftRequest = null
    },
    setSelectedStatusOption: (state: MissionsState, action: PayloadAction<RequestStatus>) => {
      state.selectedStatusOption = action.payload
      state.orderBy = action.payload === 'PENDING' ? 'created_on' : '-created_on'
    },
    setNumberOfMissionsForRequestType: (
      state: MissionsState,
      action: PayloadAction<{ statusOption: RequestStatus; numberOfMissions: number }>,
    ) => {
      const { numberOfMissions, statusOption } = action.payload

      state.numberOfMissionsConfig = {
        ...state.numberOfMissionsConfig,
        [statusOption]: numberOfMissions,
      }
    },
    setOrderBy: (state: MissionsState, action: PayloadAction<OrderBy>) => {
      state.orderBy = action.payload
    },
    setSearchQuery: (state: MissionsState, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    clearBeneficiaryIndicator: (state) => {
      state.shouldShowBeneficiaryIndicator = false
    },
  },
})

export default slice
